/**
 * @file 面包屑上下文
 * @author zhangfengzhi
 * @date 2026-01-15
 * @description 提供全局面包屑管理功能，允许子页面动态追加额外的面包屑项
 */
'use client';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

/**
 * 额外的面包屑项接口
 */
export interface BreadcrumbExtraItem {
    title: string;
}

/**
 * 面包屑上下文类型
 */
interface BreadcrumbContextType {
    extraItems: BreadcrumbExtraItem[];
    setExtraItems: (items: BreadcrumbExtraItem[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(
    undefined,
);

/**
 * 使用面包屑上下文的 Hook
 * @returns 面包屑上下文对象
 * @throws 如果不在 BreadcrumbProvider 内部使用，抛出错误
 */
export const useBreadcrumb = (): BreadcrumbContextType => {
    const context = useContext(BreadcrumbContext);
    if (!context) {
        throw new Error(
            'useBreadcrumb must be used within a BreadcrumbProvider',
        );
    }
    return context;
};

/**
 * 面包屑上下文提供者组件
 * @param children 子组件
 */
export const BreadcrumbProvider: FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [extraItems, setExtraItems] = useState<BreadcrumbExtraItem[]>([]);

    return (
        <BreadcrumbContext.Provider value={{ extraItems, setExtraItems }}>
            {children}
        </BreadcrumbContext.Provider>
    );
};
